import { Link } from "react-router-dom";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useBurgerMenu } from "../hooks/useBurgerMenu";
import {
  boardHomeUrl,
  mainSiteDashboardUrl,
  mainSiteLoginUrl,
} from "../lib/mainSiteLinks";
import { apiFetch } from "../api/client";

function UserAvatar() {
  return (
    <div className="header__user-avatar">
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden>
        <circle cx="16" cy="16" r="16" fill="#6246EA" />
        <path
          d="M16 8C18.2091 8 20 9.79086 20 12C20 14.2091 18.2091 16 16 16C13.7909 16 12 14.2091 12 12C12 9.79086 13.7909 8 16 8Z"
          fill="white"
        />
        <path d="M8 24C8 19.5817 11.5817 16 16 16C20.4183 16 24 19.5817 24 24" fill="white" />
      </svg>
    </div>
  );
}

function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.3333 14.1667L17.5 10L13.3333 5.83333"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.5 10H7.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BurgerIcon() {
  return (
    <svg width="32" height="33" viewBox="0 0 32 33" fill="none" aria-hidden>
      <path
        d="M4 11.8158H28M4 21.1492H28"
        stroke="#6246EA"
        strokeWidth="2.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LoginIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 8L16 12M16 12L12 16M16 12H3M3.33782 7C5.06687 4.01099 8.29859 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C8.29859 22 5.06687 19.989 3.33782 17"
        stroke="#6246EA"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type ProfileMenuConfig = {
  planCode?: string | null;
  accountKind?: "school" | "external";
  showTrash?: boolean;
  adminSchoolAllowed?: boolean;
  adminExternalAllowed?: boolean;
  notificationCount?: number;
};

type LoggedInProps = {
  mode: "logged-in";
  userName: string;
  dashboardHref?: string;
  logoutHref?: string;
  logoHref?: string;
  onLogout?: () => void | Promise<void>;
  profileMenu?: ProfileMenuConfig;
};

type GuestProps = {
  mode: "guest";
  loginHref?: string;
  logoHref?: string;
};

type Props = LoggedInProps | GuestProps;

/**
 * Пункты меню профиля сами по себе.
 *
 * На узком экране шапка прячет кнопки в бургер, а меню профиля открывалось
 * нажатием — и это нажатие бургер перехватывал на закрытие. Выйти из аккаунта
 * с телефона было нечем. Поэтому в бургере те же пункты показываются списком,
 * без промежуточного нажатия.
 */
export function ProfileMenuItems({
  userName,
  dashboardHref,
  logoutHref,
  onLogout,
  profileMenu,
}: Omit<LoggedInProps, "mode">) {
  const dash = dashboardHref || mainSiteDashboardUrl();
  const logout = logoutHref || boardHomeUrl();
  const isSchool = profileMenu?.accountKind === "school";

  async function handleLogout(e: ReactMouseEvent<HTMLAnchorElement>) {
    if (onLogout) {
      e.preventDefault();
      await onLogout();
      return;
    }
    try {
      await apiFetch("/api/v1/auth/logout", { method: "POST" });
    } catch {
      /* session may already be gone */
    }
    window.location.href = logout;
  }

  const planLabel =
    profileMenu?.accountKind === "school"
      ? "School"
      : (profileMenu?.planCode ?? "free").toUpperCase();

  return (
    <>
      <div className="header__profile-menu-head">
        <strong>{userName}</strong>
        <span>Тариф: {planLabel}</span>
      </div>
      {isSchool ? (
        <a className="header__profile-menu-item" href={dash} role="menuitem">
          Кабинет школы
        </a>
      ) : null}
      <Link className="header__profile-menu-item" to="/cabinet?view=recent" role="menuitem">
        Доски Bezpart
      </Link>
      {profileMenu?.showTrash ? (
        <Link className="header__profile-menu-item" to="/trash" role="menuitem">
          Корзина
        </Link>
      ) : null}
      {profileMenu?.adminSchoolAllowed ? (
        <Link className="header__profile-menu-item" to="/admin/school" role="menuitem">
          Админка школы
        </Link>
      ) : null}
      {profileMenu?.adminExternalAllowed ? (
        <Link className="header__profile-menu-item" to="/admin/external" role="menuitem">
          Админка внешних
        </Link>
      ) : null}
      <a
        className="header__profile-menu-item header__profile-menu-item--danger"
        href={logout}
        role="menuitem"
        onClick={handleLogout}
      >
        Выйти
      </a>
    </>
  );
}

function ProfileDropdown(props: Omit<LoggedInProps, "mode">) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: Event) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="header__profile-wrap" ref={wrapRef}>
      <button
        type="button"
        className="header__profile-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <UserAvatar />
        <span className="header__user-name">{props.userName}</span>
        {props.profileMenu?.notificationCount ? (
          <span className="header__notif-badge">{props.profileMenu.notificationCount}</span>
        ) : null}
      </button>
      {open ? (
        <div className="header__profile-menu" role="menu">
          <ProfileMenuItems {...props} />
        </div>
      ) : null}
    </div>
  );
}

function HeaderButtonsLoggedIn(props: Omit<LoggedInProps, "mode">) {
  if (props.profileMenu) {
    return (
      <div className="header__btns header__btns--profile">
        <ProfileDropdown {...props} />
      </div>
    );
  }

  const dash = props.dashboardHref || mainSiteDashboardUrl();
  const logout = props.logoutHref || boardHomeUrl();

  async function handleLogout(e: ReactMouseEvent<HTMLAnchorElement>) {
    if (props.onLogout) {
      e.preventDefault();
      await props.onLogout();
      return;
    }
    try {
      await apiFetch("/api/v1/auth/logout", { method: "POST" });
    } catch {
      /* session may already be gone */
    }
    window.location.href = logout;
  }

  return (
    <div className="header__btns">
      <div className="header__user-info">
        <UserAvatar />
        <a className="header__user-name" href={dash} title="Личный кабинет">
          {props.userName}
        </a>
      </div>
      <a href={logout} className="header__btn btn-main btn-logout" onClick={handleLogout}>
        <LogoutIcon />
        Выйти
      </a>
    </div>
  );
}

function HeaderButtonsGuest({ loginHref }: Omit<GuestProps, "mode">) {
  const login = loginHref || "/login";

  return (
    <div className="header__btns">
      <a href={login} className="header__btn btn-border btn-log-in">
        <LoginIcon />
        Войти
      </a>
    </div>
  );
}

export function BezpartHeader(props: Props) {
  const { open, toggle, close } = useBurgerMenu();
  const logoHref =
    props.logoHref ??
    (props.mode === "logged-in" ? "/cabinet?folder=root" : "/");
  const guest = props.mode === "guest";

  const buttons =
    props.mode === "logged-in" ? (
      <HeaderButtonsLoggedIn {...props} />
    ) : (
      <HeaderButtonsGuest {...props} />
    );

  /**
   * В бургере меню профиля показываем сразу списком. Выпадение открывается
   * нажатием, а бургер закрывается от любого нажатия внутри — и до «Выйти»
   * на телефоне было не добраться вовсе.
   */
  const burgerBody =
    props.mode === "logged-in" && props.profileMenu ? (
      <div className="burger-menu__profile" role="menu">
        <ProfileMenuItems {...props} />
      </div>
    ) : (
      buttons
    );

  return (
    <>
      <header className={`header${guest ? " header--guest" : ""}`}>
        <div className="header__container container">
          <nav className="header__inner">
            <Link to={logoHref} className="header__logo">
              {/* Знак доски — готовый фирменный, из design-system/brand.
                  Вариантов два: фиолетовый для светлого основания и кремовый
                  для тёмного. Шапка доски всегда белая, поэтому здесь первый;
                  второй лежит рядом как board-mark-light.svg. */}
              <img className="header__logo-mark" src="/brand/board-mark.svg" alt="" width="40" height="40" />
              <span className="header__logo-text">
              {/* Логотип наборный: слово строчными, фиолетовая только p.
                  Буква стоит ЧЕСТНАЯ, а не перевёрнутая b: текст правильный,
                  копируется правильно и читалка читает правильно. */}
              <span className="header__logo-name">bez<i className="header__logo-p">p</i>art</span>
              <span className="header__logo-sp">Доска</span>
              </span>
            </Link>
            <div className="header__right">
              {buttons}
              <button
                type="button"
                className="burger-btn"
                aria-label="Открыть меню"
                aria-expanded={open}
                onClick={toggle}
              >
                <BurgerIcon />
              </button>
            </div>
          </nav>
        </div>
      </header>

      <div
        className={`burger-menu${open ? " burger-menu--visible" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <div className="burger-menu__window">
          <div className="burger-menu__head">
            <div className="burger-menu__brand">bezpart · Доска</div>
            <button
              type="button"
              className="burger-menu__close"
              aria-label="Закрыть меню"
              onClick={close}
            >
              ×
            </button>
          </div>
          <div onClick={close}>{burgerBody}</div>
        </div>
      </div>
    </>
  );
}

export { mainSiteLoginUrl };
